import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { publicApi, formatAuthError } from "../lib/api";
import { Logo } from "../components/Logo";
import PasswordInput from "../components/PasswordInput";
import { PublicFooter } from "../components/PublicFooter";

/**
 * Reset password — accepts ?token=... from the email link.
 * Two password fields (new + confirm) so a mistyped temp password
 * doesn't lock anyone out twice.
 */
export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";

    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) setError("This reset link is missing its token. Please request a fresh one.");
    }, [token]);

    const tooShort = pw.length > 0 && pw.length < 12;
    const mismatch = pw2.length > 0 && pw !== pw2;
    const canSubmit = !!token && pw.length >= 12 && pw === pw2 && !submitting;

    async function onSubmit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setError("");
        setSubmitting(true);
        try {
            await publicApi.post("/auth/password-reset/confirm", { token, new_password: pw });
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 2200);
        } catch (err) {
            setError(formatAuthError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="relative min-h-screen w-full bg-[#0a0a0a] flex items-center justify-center px-6 py-28"
            data-testid="reset-password-page"
        >
            <div className="absolute top-8 left-8 z-10">
                <Logo />
            </div>
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="relative z-10 w-full max-w-[400px]"
            >
                <Link
                    to="/login"
                    data-testid="reset-back-to-login"
                    className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-500 hover:text-zinc-200 transition-colors mb-8"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to sign in
                </Link>
                <p className="text-[11.5px] uppercase tracking-[0.18em] text-zinc-500 mb-3">
                    Set a new password
                </p>
                <h1 className="text-3xl font-semibold tracking-tight text-zinc-50">
                    Choose your password
                </h1>
                <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed">
                    12 characters minimum. You'll be signed out of any other sessions.
                </p>

                {done ? (
                    <div
                        className="mt-8 rounded-lg border border-emerald-900/40 bg-emerald-950/20 p-5"
                        data-testid="reset-success"
                    >
                        <div className="flex items-start gap-3">
                            <ShieldCheck className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-[13.5px] text-emerald-100 font-medium">
                                    Password updated.
                                </p>
                                <p className="mt-1 text-[12.5px] text-emerald-200/70">
                                    Taking you to sign in…
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="reset-form">
                        <div className="space-y-2">
                            <label htmlFor="new-password" className="block text-[12px] text-zinc-400 font-medium">
                                New password
                            </label>
                            <PasswordInput
                                id="new-password"
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                autoComplete="new-password"
                                testid="reset-pw-input"
                            />
                            {tooShort && (
                                <p className="text-[12px] text-amber-300/80" data-testid="reset-pw-short">
                                    At least 12 characters.
                                </p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="confirm-password" className="block text-[12px] text-zinc-400 font-medium">
                                Confirm new password
                            </label>
                            <PasswordInput
                                id="confirm-password"
                                value={pw2}
                                onChange={(e) => setPw2(e.target.value)}
                                autoComplete="new-password"
                                testid="reset-pw2-input"
                            />
                            {mismatch && (
                                <p className="text-[12px] text-amber-300/80" data-testid="reset-pw-mismatch">
                                    Passwords don't match.
                                </p>
                            )}
                        </div>

                        {error && (
                            <div
                                className="text-[13px] text-red-400/90 border border-red-900/40 bg-red-950/30 rounded-md px-3 py-2"
                                data-testid="reset-error"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={!canSubmit}
                            data-testid="reset-submit-button"
                            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-md bg-zinc-50 text-zinc-950 text-[14px] font-medium hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Updating
                                </>
                            ) : (
                                <>
                                    Update password
                                    <ArrowRight className="h-4 w-4" />
                                </>
                            )}
                        </button>
                    </form>
                )}
            </motion.div>
            <PublicFooter prefix="reset-password" />
        </div>
    );
}
